// components/CarrinhoPrevisao.tsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { ControladorQuantidade } from '@components/produto/ControladorQuantidade';
import { useNavigate } from 'react-router-dom';
import { useCarrinhoContext } from '@contexts';
import { CarrinhoUtil, FormatUtil } from '@utils';
import { useSession } from '@hooks';
import { ROUTES } from '@types';

const CarrinhoPrevisao: React.FC = () => {
  const navigate = useNavigate();
  const { carrinho, updateQuantity, removeItem, enviarCarrinho, clearCarrinho, isLoading, error: contextError, clearError } = useCarrinhoContext();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<string | null>(null);
  const [localError, setLocalError] = useState('');
  const [responseData, setResponseData] = useState<any>(null);

  const items = CarrinhoUtil.toArray(carrinho);
  const error = contextError || localError;
  const { vendedor } = useSession();

  /*
    // Verifica se a previsão finalizou*/
    const indPrevisaoFim = vendedor?.indPrevisaoFim || '';
    const parseDataBrasileira = (dataStr: string): Date => {
      if (!dataStr) return new Date();
      const partes = dataStr.split(' ');
      const [dia, mes, ano] = partes[0].split('/');
      const hora = partes[1] || '00:00:00';
      const [hh, mm, ss = '00'] = hora.split(':');
      return new Date(
        parseInt(ano),
        parseInt(mes) - 1,
        parseInt(dia),
        parseInt(hh),
        parseInt(mm),
        parseInt(ss)
      );
    };

    const previsaoFinalizada = () => {
      if (!indPrevisaoFim) return false;
      const agora = new Date();
      const dataFim = parseDataBrasileira(indPrevisaoFim);
      return agora.getTime() > dataFim.getTime();
    };
  

  const handleAtualizarQuantidade = (codigo: string, novaQuantidade: number) => {
    // Se quantidade for 0, abre modal de confirmação de remoção
    if (novaQuantidade === 0) {
      handleRemoverItem(codigo);
    } else {
      updateQuantity(codigo, novaQuantidade);
    }
  };

  const handleRemoverItem = (codigo: string) => {
    setItemToRemove(codigo);
    setRemoveDialogOpen(true);
  };

  const handleConfirmarRemocao = async () => {
    if (!itemToRemove) {
      setRemoveDialogOpen(false);
      return;
    }

    try {
      setLocalError('');

      // Chama endpoint DELETE para remover o produto da previsão
      const indPrevisao = vendedor?.indPrevisao;
      const vendCodigo = vendedor?.vendCodigo;

      if (indPrevisao && vendCodigo) {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}deleteProdutoPrevisao/`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            previsao: indPrevisao.toString(),
            vendedor: vendCodigo,
            produto: itemToRemove,
          }),
        });

        if (!response.ok) {
          console.warn(`Aviso: API retornou ${response.status} ao remover produto da previsão. Removendo localmente.`);
          setLocalError('Produto não encontrado na previsão do servidor. Removido localmente.');
        } else {
          const data = await response.json();
          console.log('Produto removido da previsão:', data);
        }
      }

      // Remove do carrinho local independente do resultado da API
      removeItem(itemToRemove);
      setItemToRemove(null);
      setRemoveDialogOpen(false);
    } catch (error: any) {
      console.warn('Aviso ao remover item da previsão:', error);
      // Remove localmente mesmo com erro de rede/API
      removeItem(itemToRemove);
      setItemToRemove(null);
      setLocalError('Produto removido localmente. Pode não estar sincronizado com o servidor.');
      setRemoveDialogOpen(false);
    }
  };

  const handleCancelarRemocao = () => {
    setItemToRemove(null);
    setRemoveDialogOpen(false);
  };

  const handleFinalizarClick = () => {
    if (items.length === 0) {
      setLocalError('Carrinho vazio. Adicione produtos antes de finalizar.');
      return;
    }
    setDialogOpen(true);
  };

  const handleConfirmarFinalizacao = async () => {
    try {
      setLocalError('');
      clearError();
      setDialogOpen(false);

      const response = await enviarCarrinho();

      if (response.retorno === 'ok') {
        setResponseData(response);
        setSuccessDialogOpen(true);

        // Marca que a previsão foi enviada
        localStorage.setItem('indPrevisaoEnvio', '1');

        // Limpa o carrinho apenas se a previsão estiver finalizada
        if (previsaoFinalizada()) {
          clearCarrinho();
        }
      } else {
        setLocalError(response.mensagem || 'Erro ao finalizar previsão');
      }
    } catch (err: any) {
      setLocalError(err.message || 'Erro ao finalizar previsão. Tente novamente.');
      console.error(err);
    }
  };

  const handleFecharSucesso = () => {
    setSuccessDialogOpen(false);
    navigate(ROUTES.CLASSIFICACAO_PREVISAO);
  };

  if (items.length === 0 && !isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <ShoppingCartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Carrinho Vazio
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Adicione produtos para criar sua previsão de estoque
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(ROUTES.CLASSIFICACAO_PREVISAO)}
        >
          Ver Produtos
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header */}
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Carrinho de Previsão
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {items.length} {items.length === 1 ? 'produto' : 'produtos'}
        </Typography>
      </Box>

      {/* Mensagem de Erro */}
      {error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error" onClose={() => { setLocalError(''); clearError(); }}>
            {error}
          </Alert>
        </Box>
      )}

      {/* Lista de Itens */}
      <List sx={{ px: 1 }}>
        {items.map((item) => (
          <Card key={item.codigo} sx={{ mb: 2 }}>
            <ListItem
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              {/* Imagem */}
              <Avatar
                src={item.imagem ? FormatUtil.formatImageUrl(item.imagem) : undefined}
                alt={item.nome}
                sx={{ width: 60, height: 60 }}
                variant="rounded"
              />

              {/* Informações */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  {item.nome || item.codigo}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.unidade} • Peso: {item.peso || 0}kg
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Peso Total: {((item.peso || 0) * item.quantidade).toFixed(2)}kg
                </Typography>
              </Box>

              {/* Controles */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ControladorQuantidade
                  quantidade={item.quantidade}
                  onIncrement={() => handleAtualizarQuantidade(item.codigo, item.quantidade + 1)}
                  onDecrement={() => handleAtualizarQuantidade(item.codigo, item.quantidade - 1)}
                  onChange={(valor) => handleAtualizarQuantidade(item.codigo, valor)}
                  disabled={isLoading}
                  size="medium"
                  editavel
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoverItem(item.codigo)}
                  disabled={isLoading}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </ListItem>
          </Card>
        ))}
      </List>

      {/* Totais */}
      <Card sx={{ mx: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Resumo da Previsão
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Total de Itens:</Typography>
            <Typography variant="body2" fontWeight="bold">
              {carrinho.quantidadeTotal}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Peso Total:</Typography>
            <Typography variant="body2" fontWeight="bold">
              {carrinho.pesoTotal.toFixed(2)} kg
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Botão Finalizar */}
      <Box sx={{ px: 2 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={isLoading ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleFinalizarClick}
          disabled={isLoading || items.length === 0}
        >
          {isLoading ? 'Enviando...' : (previsaoFinalizada()? 'Finalizar Previsão' : 'Enviar Previsão') }
        </Button>
      </Box>

      {/* Diálogo de Confirmação */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar Previsão</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Deseja finalizar esta previsão de estoque?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Itens:</strong> {carrinho.quantidadeTotal}
            </Typography>
            <Typography variant="body2">
              <strong>Peso Total:</strong> {carrinho.pesoTotal.toFixed(2)} kg
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmarFinalizacao} variant="contained" disabled={isLoading}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmação de Remoção */}
      <Dialog open={removeDialogOpen} onClose={handleCancelarRemocao} maxWidth="xs" fullWidth>
        <DialogTitle>Remover Item</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover este item do carrinho?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelarRemocao}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmarRemocao} variant="contained" color="error">
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Sucesso */}
      <Dialog open={successDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Previsão Enviada!</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            {responseData?.mensagem || 'Previsão enviada com sucesso!'}
          </Alert>
          {responseData && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Previsão:</strong> {responseData.previsao}
              </Typography>
              <Typography variant="body2">
                <strong>Produtos:</strong> {responseData.produtos}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharSucesso} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarrinhoPrevisao;
